<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import type { User } from '@/types/auth'
import type { LegalDocumentKey } from '@/types/legal'
import { useRegistration } from '@/composables/useRegistration'
import { useParticipantAuth } from '@/composables/useParticipantAuth'
import { fetchMyRaceRegistration } from '@/api/registration'
import { sendPageView } from '@/api/pageViews'
import RegistrationWizard from '@/components/registration/RegistrationWizard.vue'
import RegistrationSummary from '@/components/registration/RegistrationSummary.vue'
import LegalDrawer from '@/components/LegalDrawer.vue'
import { setLocale, SUPPORTED } from '@/i18n'
import LanguageToggle from '@/components/public/LanguageToggle.vue'
import { buildStartlineTermsHtml } from '@/utils/startlineTermsContent'
import { buildStartlinePrivacyHtml } from '@/utils/startlinePrivacyContent'
import { useAppConfig } from '@/composables/useAppConfig'
import { FALLBACK_COMPANY } from '@/constants/company'

const route = useRoute()
const router = useRouter()
const { t } = useI18n()
const { config } = useAppConfig()

const termsHtml = computed(() =>
  buildStartlineTermsHtml(
    config.value?.email ?? { info: 'info@startline.be' },
    config.value?.company ?? FALLBACK_COMPANY,
  ),
)
const privacyHtml = computed(() =>
  buildStartlinePrivacyHtml(
    config.value?.email ?? { support: 'support@startline.be', info: 'info@startline.be' },
    config.value?.company ?? FALLBACK_COMPANY,
  ),
)

const {
  registrationData,
  currentStep,
  fieldAnswers,
  isSubmitting,
  submitError,
  registration,
  isLoading,
  loadError,
  stepLabels,
  selectedOptionsCost,
  totalAmount,
  isWaitingListMode,
  isPaymentStep,
  isZeroPayment,
  voucherCode,
  voucherDiscount,
  isVoucherLoading,
  voucherError,
  fetchRegistrationData,
  setFieldAnswer,
  nextStep,
  previousStep,
  submitRegistration,
  resumeFromRegistration,
  resetState,
  applyVoucher,
  removeVoucher,
} = useRegistration()

const { participantUser, isParticipantAuthenticated, initParticipantAuth } = useParticipantAuth()

const drawerOpen = ref(false)
const drawerInitial = ref<LegalDocumentKey>('race_rules')
const returnFocusEl = ref<HTMLElement | null>(null)

const websiteUrl = computed<string | null>(
  () => registrationData.value?.event.website_url ?? null,
)
const raceRules = computed<string | null>(
  () => registrationData.value?.edition.race_rules ?? null,
)
const termsAndConditions = computed<string | null>(
  () => registrationData.value?.edition.terms_and_conditions ?? null,
)
const hasRules = computed(() => !!raceRules.value && raceRules.value.trim() !== '')
const hasTerms = computed(() => !!termsAndConditions.value && termsAndConditions.value.trim() !== '')
// Footer legal links crowd on mobile only once all four are present (organizer
// documents + the two always-present Startline links) - split into two rows
// only then, otherwise keep the natural single-row layout.
const footerCrowded = computed<boolean>(() => hasRules.value && hasTerms.value)

function openLegalDrawer(doc: LegalDocumentKey, e: MouseEvent | KeyboardEvent): void {
  drawerInitial.value = doc
  returnFocusEl.value = e.currentTarget as HTMLElement
  drawerOpen.value = true
}

const orgSlug = String(route.params['orgSlug'])
const eventSlug = String(route.params['eventSlug'])
const raceSlug = String(route.params['raceSlug'])

onMounted(async () => {
  // Captured before the ?lang cleanup below so UTM values are read from the entry URL
  const entryQuery = { ...route.query }

  // Apply ?lang param from the event-site handoff, then clean it from the URL
  const langParam = route.query['lang']
  if (typeof langParam === 'string' && (SUPPORTED as readonly string[]).includes(langParam)) {
    setLocale(langParam)
    const { lang: _removed, ...restQuery } = route.query
    void router.replace({ query: restQuery })
  }

  const [, user] = await Promise.all([
    fetchRegistrationData(orgSlug, eventSlug, raceSlug),
    initParticipantAuth(),
  ])

  if (registrationData.value !== null) {
    sendPageView(
      {
        type: 'page_view.registration_form',
        org_slug: orgSlug,
        event_slug: eventSlug,
        race_slug: raceSlug,
      },
      entryQuery,
    )
  }

  if (user) {
    const raceId = registrationData.value?.race.id
    if (raceId !== undefined) {
      const existing = await fetchMyRaceRegistration(raceId)
      if (existing) {
        resumeFromRegistration(existing)
      }
    }
  }
})

onUnmounted(() => {
  resetState()
})

function handleAuthenticated(_user: User): void {
  // The wizard manages step advancement:
  // - After register: wizard auto-advances to next step
  // - After login: wizard stays on step 0 for profile review
}

async function handleLoggedIn(_user: User): Promise<void> {
  // Post-login resume re-check (spec §6.5): a returning participant who logs
  // in mid-wizard may already have a registration for this exact race. Surface
  // it via the wizard's own resume mechanism instead of letting them hit a
  // late duplicate-registration 422 at submit time. This is a UX nicety only —
  // fail silently on any error and let the participant continue the wizard.
  const raceId = registrationData.value?.race.id
  if (raceId === undefined) return
  try {
    const existing = await fetchMyRaceRegistration(raceId)
    if (existing) {
      resumeFromRegistration(existing)
    }
  } catch {
    // Ignore — resume re-check failures must never block the registration flow.
  }
}

async function handleSubmit(): Promise<void> {
  if (!registrationData.value) return
  try {
    await submitRegistration(registrationData.value.race.id)
  } catch {
    // Error is surfaced via the registration composable's isSubmitting state resetting to false
    // A general error message can be added here if needed
  }
}

function handleApplyVoucher(code: string): void {
  const raceId = registrationData.value?.race.id
  if (raceId !== undefined) {
    void applyVoucher(code, raceId)
  }
}

function handleRemoveVoucher(): void {
  removeVoucher()
}

// Show voucher input only when the event has active vouchers and we are not on the payment step
const showVoucher = computed(
  () => (registrationData.value?.has_active_vouchers ?? false) && !isPaymentStep.value,
)
</script>

<template>
  <div class="min-h-screen bg-slate-50 flex flex-col">
    <!-- Loading state -->
    <div v-if="isLoading" class="flex items-center justify-center min-h-screen">
      <svg class="animate-spin h-10 w-10 text-emerald-500" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
      </svg>
    </div>

    <!-- Error: not found -->
    <div
      v-else-if="loadError && !registrationData"
      class="flex items-center justify-center min-h-screen px-4"
    >
      <div class="max-w-md w-full text-center bg-white rounded-2xl border border-slate-200 p-10">
        <div class="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
          <svg class="w-7 h-7 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h1 class="text-xl font-bold text-slate-900 mb-2">{{ t('registration.race_not_found') }}</h1>
      </div>
    </div>

    <!-- Registrations closed -->
    <div
      v-else-if="registrationData && registrationData.race.is_closed"
      class="flex items-center justify-center min-h-screen px-4"
    >
      <div class="max-w-md w-full text-center bg-white rounded-2xl border border-slate-200 p-10">
        <div class="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
          <svg class="w-7 h-7 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <h1 class="text-xl font-bold text-slate-900 mb-2">{{
          registrationData.race.registration_availability === 'not_yet_open'
            ? t('registration.not_yet_open')
            : t('registration.registrations_closed')
        }}</h1>
        <p class="text-sm text-slate-500">{{ registrationData.event.name }} — {{ registrationData.race.name }}</p>
      </div>
    </div>

    <!-- Main registration page -->
    <template v-else-if="registrationData">
      <!-- Page header -->
      <div class="bg-white border-b border-slate-200">
        <div class="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:pt-6 sm:pb-3">
          <div class="flex items-start justify-between gap-3 sm:gap-4">
            <!-- Identity: logo + title. The "Back to event" link used to live
                 here (stacked under the logo, or beneath the title on mobile)
                 but now lives in its own card below "Organised by" in
                 RegistrationSummary.vue instead, so the header only carries
                 the event/race identity. -->
            <div class="flex min-w-0 flex-1 items-center gap-3 sm:gap-4">
              <img
                v-if="registrationData.event.logo_url"
                :src="registrationData.event.logo_url"
                :alt="registrationData.event.name"
                class="w-14 h-14 rounded-xl object-cover border border-slate-200 shrink-0 sm:w-20 sm:h-20"
              />
              <div
                v-else
                class="w-14 h-14 rounded-xl bg-slate-100 flex items-center justify-center shrink-0 sm:w-20 sm:h-20"
              >
                <svg class="w-6 h-6 text-slate-400 sm:w-10 sm:h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>

              <div class="min-w-0">
                <h1 class="text-xl font-bold text-slate-900 leading-tight sm:text-3xl mb-1">
                  {{ t('registration.page_title', { raceName: registrationData.race.name }) }}
                </h1>
                <p class="text-xs font-medium text-slate-500 uppercase tracking-wide">
                  {{ registrationData.event.name }}
                  <a
                    v-if="websiteUrl"
                    :href="websiteUrl"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="inline-flex align-middle text-emerald-600 hover:text-emerald-700 transition-colors"
                    :aria-label="t('common.visit_event_website')"
                    :title="t('common.visit_event_website')"
                  >
                    <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                  · {{ registrationData.edition.name }}
                </p>
              </div>
            </div>

            <!-- Right-side controls: language toggle + participant welcome.
                 Signed out, the column holds only the compact dropdown
                 trigger and does not need the wider signed-in width. -->
            <div
              class="flex shrink-0 flex-col items-end gap-1.5 sm:w-auto sm:gap-2 sm:self-center sm:pl-4"
              :class="isParticipantAuthenticated ? 'w-28' : 'w-16'"
            >
              <LanguageToggle />
              <template v-if="isParticipantAuthenticated">
                <div class="flex w-full flex-col items-end gap-1.5 sm:w-auto sm:gap-2">
                  <span class="w-full truncate text-right text-xs text-slate-600 sm:w-auto sm:text-sm">
                    {{ t('nav.welcome', { name: participantUser?.first_name }) }}
                  </span>
                  <RouterLink
                    to="/my/races"
                    class="inline-flex w-full items-center justify-center gap-1.5 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-lg px-2 py-1.5 text-xs leading-tight transition-colors sm:w-auto sm:whitespace-nowrap sm:px-4 sm:py-2 sm:text-sm"
                  >
                    {{ t('nav.my_dashboard') }}
                  </RouterLink>
                </div>
              </template>
            </div>
          </div>
        </div>

      </div>

      <!-- Two-column layout -->
      <div class="w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div class="lg:grid lg:grid-cols-3 lg:gap-8">
          <!-- Wizard column (2/3) -->
          <div class="lg:col-span-2">
            <RegistrationWizard
              :registration-data="registrationData"
              :field-answers="fieldAnswers"
              :current-step="currentStep"
              :step-labels="stepLabels"
              :is-submitting="isSubmitting"
              :submit-error="submitError"
              :registration="registration"
              :org-slug="orgSlug"
              :event-slug="eventSlug"
              :race-slug="raceSlug"
              :is-waiting-list-mode="isWaitingListMode"
              :is-zero-payment="isZeroPayment"
              :voucher-code="voucherCode"
              :race-rules="raceRules"
              :terms-and-conditions="termsAndConditions"
              @authenticated="handleAuthenticated"
              @logged-in="handleLoggedIn"
              @update:answer="(id, val, opts) => setFieldAnswer(id, val, opts)"
              @next="nextStep"
              @back="previousStep"
              @submit="handleSubmit"
              @open-legal-drawer="openLegalDrawer"
            />
          </div>

          <!-- Summary sidebar (1/3, sticky) -->
          <div class="hidden lg:block">
            <div class="sticky top-8">
              <RegistrationSummary
                :registration-data="registrationData"
                :field-answers="fieldAnswers"
                :selected-options-cost="selectedOptionsCost"
                :total-amount="totalAmount"
                :organiser="registrationData.organiser"
                :org-slug="orgSlug"
                :event-slug="eventSlug"
                :voucher-code="voucherCode"
                :voucher-discount="voucherDiscount"
                :is-voucher-loading="isVoucherLoading"
                :voucher-error="voucherError"
                :show-voucher="showVoucher"
                @apply-voucher="handleApplyVoucher"
                @remove-voucher="handleRemoveVoucher"
              />
            </div>
          </div>
        </div>

        <!-- Mobile summary (below wizard) -->
        <div class="lg:hidden mt-6">
          <RegistrationSummary
            :registration-data="registrationData"
            :field-answers="fieldAnswers"
            :selected-options-cost="selectedOptionsCost"
            :total-amount="totalAmount"
            :organiser="registrationData.organiser"
            :org-slug="orgSlug"
            :event-slug="eventSlug"
            :voucher-code="voucherCode"
            :voucher-discount="voucherDiscount"
            :is-voucher-loading="isVoucherLoading"
            :voucher-error="voucherError"
            :show-voucher="showVoucher"
            @apply-voucher="handleApplyVoucher"
            @remove-voucher="handleRemoveVoucher"
          />
        </div>
      </div>

      <!-- Footer -->
      <div class="border-t border-slate-200 py-4">
        <div class="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p class="text-center text-xs text-slate-400">
            {{ t('nav.powered_by') }}
            <a
              href="https://startline.be"
              target="_blank"
              rel="noopener noreferrer"
              class="font-black text-slate-500 hover:text-slate-700 transition-colors"
            >Startline<span class="inline-block w-1 h-1 rounded-full bg-emerald-400 ml-0.5 align-middle"></span></a>
          </p>
          <div
            class="flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5 text-xs text-slate-400 sm:flex-nowrap sm:justify-start sm:gap-4"
          >
            <button
              v-if="hasRules"
              type="button"
              class="hover:text-slate-600 transition-colors bg-transparent border-0 p-0 cursor-pointer"
              @click="openLegalDrawer('race_rules', $event)"
            >
              {{ t('footer.race_rules') }}
            </button>
            <button
              v-if="hasTerms"
              type="button"
              class="hover:text-slate-600 transition-colors bg-transparent border-0 p-0 cursor-pointer"
              @click="openLegalDrawer('terms_and_conditions', $event)"
            >
              {{ t('footer.event_terms') }}
            </button>
            <!-- Zero-height spacer that forces the two organizer-document
                 buttons above onto their own row on mobile. Relies on DOM
                 order alone (it sits between them and the two Startline
                 links below) - no order-* utilities needed here or on the
                 buttons around it. -->
            <span
              v-if="footerCrowded"
              class="-mt-1.5 h-0 basis-full sm:hidden"
              aria-hidden="true"
            ></span>
            <button
              type="button"
              class="hover:text-slate-600 transition-colors bg-transparent border-0 p-0 cursor-pointer"
              @click="openLegalDrawer('privacy_policy', $event)"
            >{{ t('footer.privacy_policy') }}</button>
            <button
              type="button"
              class="hover:text-slate-600 transition-colors bg-transparent border-0 p-0 cursor-pointer"
              @click="openLegalDrawer('startline_terms', $event)"
            >{{ t('footer.startline_terms') }}</button>
          </div>
        </div>
      </div>
    </template>

    <LegalDrawer
      v-model:open="drawerOpen"
      :initial-document="drawerInitial"
      :race-rules="raceRules"
      :terms-and-conditions="termsAndConditions"
      :startline-terms="termsHtml"
      :privacy-policy="privacyHtml"
      :return-focus-to="returnFocusEl"
    />
  </div>
</template>
